import { InvoiceVersion } from '$constants'
import {
  IInvoice,
  IItem,
  IItemSupplierVariant,
  ItemSupplierVariant,
} from '$models'

export const isProcessedInvoice = (doc: IInvoice) =>
  doc.version !== InvoiceVersion.Processing

/**
 * Checks if a document is a CreditNote
 * @param doc the document to check
 * @returns true is document is a Credit Note, false if not
 */
export const isCreditNote = (doc: IInvoice) => {
  return (
    doc.version !== InvoiceVersion.Failed &&
    doc.lines?.every(
      (line) => line.quantityReceived && line.quantityReceived < 0
    )
  )
}

/**
 * Searches the item store and updates or adds the given item variant
 * NOTE: This method does not support unsetting the default for supplier on the variant you are trying to update.
 * If you want to unset the default for a variant you MUST set a new varaint to be the default before unsetting
 * the variant (but the backend should handle unsetting the varaint for you).
 * @param itemStore the item store
 * @param variant the variant to update/add
 * @returns the updated item store
 */
export const updateOrAddItemSupplierVariantWithinItemStore = (
  itemStore: Record<string, IItem>,
  variant: IItemSupplierVariant
): Record<string, IItem> => {
  const itemsCopy = { ...itemStore }

  const idxOfVariant =
    itemStore[variant.stockItemId!].suppliers?.findIndex(
      (v) => v.id === variant.id
    ) ?? -1

  if (idxOfVariant === -1) {
    itemsCopy[variant.stockItemId!].suppliers?.push(
      new ItemSupplierVariant(variant)
    )
    return itemsCopy
  }

  // If the variant being updated is the default for supplier. Make sure no other supplier variants for this item are default.
  if (variant.defaultForSupplier) {
    itemsCopy[variant.stockItemId!].suppliers?.forEach((v) => {
      if (v.supplierId === variant.supplierId && v.id !== variant.id) {
        v.defaultForSupplier = false
      }
    })
  }

  // Another note about unsetting default variants: We need to figure out how to update the variants accurately when a the updated variant
  // went from default to not default. As the backend currently just picks the first variant it finds as the default it is difficult to
  // know which variant should be the default here in the front end. Currently this scenario is not possible in happen in UI so I am
  // leaving this to be fixed in the future OR ideally you set a new default varaint before unsetting the default as the backend handles it
  // for you.

  itemsCopy[variant.stockItemId!].suppliers![idxOfVariant] =
    new ItemSupplierVariant(variant)

  return itemsCopy
}
